import React, { useState, useEffect, useMemo, useRef } from "react";
import axios from "axios";
import {
  Clock, Shield, AlertTriangle, GitBranch, Users, TrendingUp, TrendingDown,
  ChevronRight, ArrowUpRight, Plus, Activity, Layers, Target, Zap,
  BarChart3, Radio, Mail, FileSearch, AlertOctagon, Scale, Gavel,
  CheckCircle2, XCircle, CalendarClock, Briefcase, DollarSign, Eye
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../components/ui/tooltip";
import ExportToolbar from "../components/ExportToolbar";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer,
  Cell, PieChart, Pie, Tooltip as RechartsTooltip, LineChart, Line, Legend
} from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ─── Animated Counter ─── */
const AnimatedNumber = ({ value, duration = 1200, prefix = "", suffix = "" }) => {
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    if (typeof value !== "number" || isNaN(value)) { setDisplay(value); return; }
    let start = 0;
    const step = value / (duration / 16);
    const id = setInterval(() => {
      start += step;
      if (start >= value) { setDisplay(value); clearInterval(id); }
      else setDisplay(Math.round(start));
    }, 16);
    return () => clearInterval(id);
  }, [value, duration]);
  return <span>{prefix}{typeof display === "number" ? display.toLocaleString() : display}{suffix}</span>;
};

/* ─── Sparkline ─── */
const Sparkline = ({ data, color = "#E11D48", height = 32 }) => {
  const ref = useRef(null);
  useEffect(() => {
    const c = ref.current;
    if (!c || !data?.length) return;
    const ctx = c.getContext("2d");
    const w = (c.width = c.offsetWidth * 2);
    const h = (c.height = height * 2);
    ctx.scale(2, 2);
    const cw = w / 2, ch = h / 2;
    const max = Math.max(...data), min = Math.min(...data);
    const range = max - min || 1;
    ctx.clearRect(0, 0, cw, ch);
    const grad = ctx.createLinearGradient(0, 0, 0, ch);
    grad.addColorStop(0, color + "30");
    grad.addColorStop(1, color + "05");
    ctx.beginPath();
    data.forEach((v, i) => {
      const x = (i / (data.length - 1)) * cw;
      const y = ch - ((v - min) / range) * (ch - 4) - 2;
      if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
    });
    ctx.lineTo(cw, ch); ctx.lineTo(0, ch);
    ctx.fillStyle = grad; ctx.fill();
    ctx.beginPath();
    data.forEach((v, i) => {
      const x = (i / (data.length - 1)) * cw;
      const y = ch - ((v - min) / range) * (ch - 4) - 2;
      if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
    });
    ctx.strokeStyle = color; ctx.lineWidth = 1.5; ctx.stroke();
  }, [data, color, height]);
  return <canvas ref={ref} style={{ width: "100%", height }} />;
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-zinc-900 text-white px-3 py-2 text-xs font-medium border border-zinc-700">
      <p className="font-bold uppercase tracking-wide mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color || p.fill }}>
          {p.name}: {p.value}
        </p>
      ))}
    </div>
  );
};

const CLAIM_COLORS = { submitted: "#EAB308", under_review: "#F59E0B", approved: "#10B981", rejected: "#DC2626", draft: "#71717A" };
const STATUS_COLORS = ["#10B981", "#F59E0B", "#E11D48", "#DC2626"];

/* ═══════════════════════════════════════════════
   FORENSIC DELAY ANALYSIS DASHBOARD
   ═══════════════════════════════════════════════ */
const DashboardView = ({ setActiveView, contracts, clauses }) => {
  const [animReady, setAnimReady] = useState(false);
  const [schedules, setSchedules] = useState([]);
  const [changeOrders, setChangeOrders] = useState([]);
  const [delayEvents, setDelayEvents] = useState([]);
  const [loadingMeta, setLoadingMeta] = useState(true);

  useEffect(() => {
    const t = setTimeout(() => setAnimReady(true), 100);
    return () => clearTimeout(t);
  }, []);

  // Fetch forensic-specific data (best-effort: ignore errors / missing endpoints)
  useEffect(() => {
    const loadMeta = async () => {
      setLoadingMeta(true);
      const results = await Promise.allSettled([
        axios.get(`${API}/pm-integration/schedules`).catch(() => ({ data: { schedules: [] } })),
        axios.get(`${API}/change-orders`).catch(() => ({ data: [] })),
        axios.get(`${API}/delay-events`).catch(() => ({ data: [] })),
      ]);
      setSchedules(results[0].status === "fulfilled" ? (results[0].value.data.schedules || []) : []);
      setChangeOrders(results[1].status === "fulfilled" ? (Array.isArray(results[1].value.data) ? results[1].value.data : []) : []);
      setDelayEvents(results[2].status === "fulfilled" ? (Array.isArray(results[2].value.data) ? results[2].value.data : []) : []);
      setLoadingMeta(false);
    };
    loadMeta();
  }, []);

  /* ─── Computed Forensic Stats ─── */
  const stats = useMemo(() => {
    const portfolioRisk = contracts.length > 0
      ? Math.round(contracts.reduce((s, c) => s + (c.risk_score || 0), 0) / contracts.length)
      : 0;
    const highRiskContracts = contracts.filter(c => (c.risk_score || 0) > 70).length;
    const totalDelayDays = delayEvents.reduce((s, d) => s + (d.delay_days || d.impact_days || 0), 0);
    const activeClaims = changeOrders.filter(c => ["submitted", "under_review"].includes(c.status)).length;
    const approvedClaims = changeOrders.filter(c => c.status === "approved").length;
    const rejectedClaims = changeOrders.filter(c => c.status === "rejected").length;
    const totalClaimValue = changeOrders.reduce((s, c) => s + (c.claim_value || c.cost_impact || 0), 0);
    const criticalProgrammes = schedules.filter(s => (s.critical_activities || 0) > 0).length;
    return {
      portfolioRisk, highRiskContracts, totalDelayDays, activeClaims, approvedClaims,
      rejectedClaims, totalClaimValue, criticalProgrammes
    };
  }, [contracts, schedules, changeOrders, delayEvents]);

  /* ─── Risk Distribution ─── */
  const riskDistribution = useMemo(() => {
    const bins = [
      { name: "Low", range: [0, 30], count: 0, fill: "#10B981" },
      { name: "Medium", range: [31, 55], count: 0, fill: "#F59E0B" },
      { name: "High", range: [56, 75], count: 0, fill: "#E11D48" },
      { name: "Critical", range: [76, 100], count: 0, fill: "#DC2626" },
    ];
    contracts.forEach(c => {
      const r = c.risk_score || 0;
      const bin = bins.find(b => r >= b.range[0] && r <= b.range[1]);
      if (bin) bin.count++;
    });
    return bins;
  }, [contracts]);

  /* ─── Claim Pipeline ─── */
  const claimPipeline = useMemo(() => {
    const buckets = { draft: 0, submitted: 0, under_review: 0, approved: 0, rejected: 0 };
    changeOrders.forEach(c => { if (buckets[c.status] !== undefined) buckets[c.status]++; });
    return Object.entries(buckets).map(([status, value]) => ({ name: status.replace(/_/g, " "), value, fill: CLAIM_COLORS[status] }));
  }, [changeOrders]);

  /* ─── Delay Trend (monthly) ─── */
  const delayTrend = useMemo(() => {
    // synthesize smooth trend from delayEvents; fallback if empty
    if (!delayEvents.length) {
      return Array.from({ length: 6 }, (_, i) => ({
        month: ["M-5", "M-4", "M-3", "M-2", "M-1", "Now"][i],
        cumulative: 0, newDelay: 0,
      }));
    }
    const byMonth = {};
    delayEvents.forEach(d => {
      const dt = new Date(d.event_date || d.created_at || Date.now());
      const key = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}`;
      byMonth[key] = (byMonth[key] || 0) + (d.delay_days || d.impact_days || 0);
    });
    const keys = Object.keys(byMonth).sort().slice(-6);
    let cum = 0;
    return keys.map(k => { cum += byMonth[k]; return { month: k, cumulative: cum, newDelay: byMonth[k] }; });
  }, [delayEvents]);

  const riskSparkData = useMemo(() => {
    const base = stats.portfolioRisk || 30;
    return Array.from({ length: 12 }, (_, i) =>
      Math.max(5, Math.round(base + Math.sin(i * 0.8) * 15 + (Math.random() - 0.5) * 10))
    );
  }, [stats.portfolioRisk]);

  const delay = (i) => ({ animationDelay: `${i * 80}ms` });

  /* ─── KPI CARDS DATA ─── */
  const kpiCards = [
    {
      id: "delay",
      label: "Total Delay Days",
      value: stats.totalDelayDays,
      icon: Clock,
      color: "#E11D48",
      sub: `${delayEvents.length} delay events`,
      target: "delay-analysis",
    },
    {
      id: "claims",
      label: "Active Claims",
      value: stats.activeClaims,
      icon: Scale,
      color: "#EAB308",
      sub: `${stats.approvedClaims} approved • ${stats.rejectedClaims} rejected`,
      target: "changes",
    },
    {
      id: "risk",
      label: "Portfolio Risk",
      value: stats.portfolioRisk,
      icon: Shield,
      color: stats.portfolioRisk > 60 ? "#DC2626" : stats.portfolioRisk > 40 ? "#F59E0B" : "#10B981",
      suffix: "/100",
      spark: riskSparkData,
      sub: `${stats.highRiskContracts} high-risk`,
      target: "risk-analysis",
    },
    {
      id: "programmes",
      label: "Programmes",
      value: schedules.length,
      icon: Layers,
      color: "#8B5CF6",
      sub: `${stats.criticalProgrammes} with critical path`,
      target: "pm-integration",
    },
  ];

  return (
    <TooltipProvider>
      <div className="space-y-6" data-testid="dashboard-view" data-export-region>

        {/* Export bar */}
        <div className="flex justify-end" data-no-print>
          <ExportToolbar title="Forensic Dashboard Overview" pdfFilename="forensic-dashboard" />
        </div>

        {/* ── HERO BANNER ── */}
        <div
          className={`relative overflow-hidden border-2 border-black bg-zinc-950 text-white p-8 md:p-10 brutal-shadow-lg transition-all duration-700 ${animReady ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}
          style={delay(0)}
          data-testid="dashboard-hero"
        >
          <div className="absolute inset-0 opacity-[0.03]"
            style={{
              backgroundImage: "repeating-linear-gradient(0deg, transparent, transparent 40px, #fff 40px, #fff 41px), repeating-linear-gradient(90deg, transparent, transparent 40px, #fff 40px, #fff 41px)"
            }}
          />
          <div className="absolute top-0 right-0 w-64 h-64 bg-[#E11D48] opacity-10 blur-[100px]" />
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-[#EAB308] opacity-10 blur-[80px]" />

          <div className="relative z-10">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-2 h-2 bg-[#10B981] animate-pulse" />
              <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-400">Forensic Engine Active</span>
              <Badge className="bg-[#E11D48] text-white border-none text-[10px] uppercase tracking-widest px-2 py-0">
                v3.2
              </Badge>
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight uppercase font-['Chivo'] mb-3 leading-[1.1]">
              Forensic<br />
              <span className="text-[#E11D48]">Delay</span> Control<span className="text-[#EAB308]">.</span>
            </h1>
            <p className="text-sm text-zinc-400 max-w-xl mb-6 leading-relaxed">
              Baseline-vs-baseline programme forensics, EoT &amp; disruption claim tracking, risk &amp; change
              management — one command surface for your entire claims portfolio.
            </p>
            <div className="flex flex-wrap gap-3">
              <Button
                onClick={() => setActiveView("delay-analysis")}
                className="btn-brutal bg-[#E11D48] hover:bg-[#BE123C] text-white px-5 py-2.5 h-auto text-xs"
                data-testid="cta-delay-analysis"
              >
                <Clock size={14} className="mr-2" />
                Run Delay Analysis
              </Button>
              <Button
                onClick={() => setActiveView("pm-integration")}
                variant="outline"
                className="btn-brutal border border-zinc-600 text-zinc-300 hover:text-white hover:border-white px-5 py-2.5 h-auto text-xs bg-transparent"
                data-testid="cta-import-programme"
              >
                <Layers size={14} className="mr-2" />
                Import / Compare Programmes
              </Button>
              <Button
                onClick={() => setActiveView("analyzer")}
                variant="outline"
                className="btn-brutal border border-zinc-600 text-zinc-300 hover:text-white hover:border-white px-5 py-2.5 h-auto text-xs bg-transparent"
                data-testid="cta-contract-import"
              >
                <FileSearch size={14} className="mr-2" />
                Analyze Contract
              </Button>
              <Button
                onClick={() => setActiveView("changes")}
                variant="outline"
                className="btn-brutal border border-zinc-600 text-zinc-300 hover:text-white hover:border-white px-5 py-2.5 h-auto text-xs bg-transparent"
                data-testid="cta-claims"
              >
                <Scale size={14} className="mr-2" />
                Claim Register
              </Button>
            </div>
          </div>
        </div>

        {/* ── KPI CARDS ── */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {kpiCards.map((kpi, i) => {
            const Icon = kpi.icon;
            return (
              <button
                key={kpi.id}
                onClick={() => setActiveView(kpi.target)}
                className={`group relative text-left border-2 border-zinc-200 bg-white p-5 transition-all duration-300 hover:border-black hover:shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover:-translate-x-0.5 hover:-translate-y-0.5 ${
                  animReady ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
                }`}
                style={{ transitionDelay: `${(i + 1) * 100}ms` }}
                data-testid={`kpi-${kpi.id}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="p-2 border border-zinc-200 group-hover:border-black transition-colors">
                    <Icon size={16} style={{ color: kpi.color }} />
                  </div>
                  {kpi.spark && (
                    <div className="w-16 opacity-60 group-hover:opacity-100 transition-opacity">
                      <Sparkline data={kpi.spark} color={kpi.color} height={24} />
                    </div>
                  )}
                </div>
                <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500 mb-1">{kpi.label}</p>
                <p className="text-2xl lg:text-3xl font-black font-['Chivo'] leading-none">
                  <AnimatedNumber value={kpi.value} />{kpi.suffix || ""}
                </p>
                <p className="text-[11px] text-zinc-400 mt-1.5">{kpi.sub}</p>
                <div
                  className="absolute top-0 right-0 w-0 h-0 border-t-[20px] border-l-[20px] border-l-transparent transition-all group-hover:border-t-[24px] group-hover:border-l-[24px]"
                  style={{ borderTopColor: kpi.color }}
                />
              </button>
            );
          })}
        </div>

        {/* ── CHARTS ROW: Delay Trend + Claim Pipeline ── */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
          {/* Delay Trend - 3 cols */}
          <div
            className={`lg:col-span-3 border-2 border-zinc-200 bg-white p-6 transition-all duration-500 ${animReady ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}
            style={{ transitionDelay: "500ms" }}
            data-testid="delay-trend-chart"
          >
            <div className="flex items-center justify-between mb-5">
              <div>
                <h2 className="text-sm font-black font-['Chivo'] uppercase tracking-wide">Cumulative Delay Trend</h2>
                <p className="text-[11px] text-zinc-400 mt-0.5">{delayEvents.length} delay events tracked • last 6 periods</p>
              </div>
              <Badge className="bg-[#E11D48]/10 text-[#E11D48] border border-[#E11D48]/30 text-[10px] uppercase tracking-wider">
                {stats.totalDelayDays}d total
              </Badge>
            </div>
            {delayTrend.some(d => d.cumulative > 0) ? (
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={delayTrend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" vertical={false} />
                  <XAxis dataKey="month" tick={{ fontSize: 10, fontFamily: "Chivo", fill: "#71717a" }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 10, fontFamily: "Chivo", fill: "#71717a" }} axisLine={false} tickLine={false} />
                  <RechartsTooltip content={<CustomTooltip />} />
                  <Legend wrapperStyle={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.1em" }} />
                  <Line type="monotone" dataKey="cumulative" stroke="#E11D48" strokeWidth={2.5} dot={{ r: 3, fill: "#E11D48" }} name="Cumulative Days" />
                  <Line type="monotone" dataKey="newDelay" stroke="#EAB308" strokeWidth={1.5} strokeDasharray="4 2" dot={{ r: 2 }} name="New Delay" />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[220px] flex flex-col items-center justify-center border border-dashed border-zinc-200">
                <CalendarClock size={32} className="text-zinc-300 mb-2" />
                <p className="text-xs text-zinc-400 uppercase tracking-wide">No delay events logged yet</p>
                <Button onClick={() => setActiveView("delay-analysis")} className="mt-3 text-[10px] px-3 py-1.5 h-auto bg-black text-white" data-testid="empty-log-delay">
                  <Plus size={10} className="mr-1" /> Log First Delay Event
                </Button>
              </div>
            )}
          </div>

          {/* Claim Pipeline - 2 cols */}
          <div
            className={`lg:col-span-2 border-2 border-zinc-200 bg-white p-6 transition-all duration-500 ${animReady ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}
            style={{ transitionDelay: "600ms" }}
            data-testid="claim-pipeline-chart"
          >
            <h2 className="text-sm font-black font-['Chivo'] uppercase tracking-wide mb-1">Claim Pipeline</h2>
            <p className="text-[11px] text-zinc-400 mb-4">By status • {changeOrders.length} total</p>
            {changeOrders.length > 0 ? (
              <>
                <ResponsiveContainer width="100%" height={160}>
                  <PieChart>
                    <Pie
                      data={claimPipeline.filter(c => c.value > 0)}
                      cx="50%" cy="50%"
                      innerRadius={42} outerRadius={66}
                      dataKey="value"
                      stroke="#fff"
                      strokeWidth={2}
                    >
                      {claimPipeline.filter(c => c.value > 0).map((entry, idx) => (
                        <Cell key={idx} fill={entry.fill} />
                      ))}
                    </Pie>
                    <RechartsTooltip content={<CustomTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="space-y-1 mt-2">
                  {claimPipeline.map((c) => (
                    <div key={c.name} className="flex items-center justify-between text-[10px]">
                      <div className="flex items-center gap-1.5">
                        <div className="w-2.5 h-2.5" style={{ backgroundColor: c.fill }} />
                        <span className="font-medium text-zinc-600 uppercase tracking-wide">{c.name}</span>
                      </div>
                      <span className="font-black font-['Chivo']">{c.value}</span>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="h-[180px] flex flex-col items-center justify-center border border-dashed border-zinc-200">
                <Scale size={28} className="text-zinc-300 mb-2" />
                <p className="text-[11px] text-zinc-400 uppercase tracking-wide">No claims filed yet</p>
                <Button onClick={() => setActiveView("changes")} className="mt-3 text-[10px] px-3 py-1.5 h-auto bg-[#EAB308] text-white" data-testid="empty-create-claim">
                  <Plus size={10} className="mr-1" /> File First Claim
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* ── RISK DISTRIBUTION + QUICK ACTIONS ── */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Risk Distribution */}
          <div
            className={`border-2 border-zinc-200 bg-white p-6 transition-all duration-500 ${animReady ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}
            style={{ transitionDelay: "700ms" }}
            data-testid="risk-distribution-chart"
          >
            <h2 className="text-sm font-black font-['Chivo'] uppercase tracking-wide">Risk Exposure</h2>
            <p className="text-[11px] text-zinc-400 mb-4">{contracts.length} contracts analyzed</p>
            {contracts.length > 0 ? (
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={riskDistribution} barSize={36}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" vertical={false} />
                  <XAxis dataKey="name" tick={{ fontSize: 10, fontFamily: "Chivo", fill: "#71717a" }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 10, fontFamily: "Chivo", fill: "#71717a" }} axisLine={false} tickLine={false} allowDecimals={false} />
                  <RechartsTooltip content={<CustomTooltip />} />
                  <Bar dataKey="count" name="Contracts">
                    {riskDistribution.map((entry, idx) => (<Cell key={idx} fill={entry.fill} />))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[180px] flex flex-col items-center justify-center border border-dashed border-zinc-200">
                <AlertOctagon size={28} className="text-zinc-300 mb-2" />
                <p className="text-[11px] text-zinc-400 uppercase tracking-wide">No contracts yet</p>
                <Button onClick={() => setActiveView("analyzer")} className="mt-3 text-[10px] px-3 py-1.5 h-auto bg-black text-white" data-testid="empty-import-contract">
                  <Plus size={10} className="mr-1" /> Import Contract
                </Button>
              </div>
            )}
          </div>

          {/* Quick Actions */}
          <div
            className={`border-2 border-zinc-200 bg-white transition-all duration-500 ${animReady ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}
            style={{ transitionDelay: "800ms" }}
            data-testid="quick-actions"
          >
            <div className="px-5 py-4 border-b-2 border-zinc-200 bg-zinc-950 text-white">
              <h2 className="text-sm font-black font-['Chivo'] uppercase tracking-wide flex items-center gap-2">
                <Zap size={14} className="text-[#E11D48]" />
                Forensic Actions
              </h2>
            </div>
            <div className="p-3 space-y-1">
              {[
                { id: "delay-analysis", icon: Clock, label: "Delay Analysis", desc: "CPM / windows / impacted AP", color: "#E11D48" },
                { id: "pm-integration", icon: Layers, label: "Programme Compare", desc: "Baseline vs. baseline + AI narrative", color: "#8B5CF6" },
                { id: "claims", icon: Gavel, label: "Claim Prevention", desc: "Contractual hooks & notice triggers", color: "#EAB308" },
                { id: "changes", icon: GitBranch, label: "Change Register", desc: "Variation & EoT tracker", color: "#10B981" },
                { id: "risk-analysis", icon: Shield, label: "Risk Analysis", desc: "Portfolio heat-map", color: "#F59E0B" },
                { id: "realtime", icon: Radio, label: "Live Collab", desc: "Multi-user evidence room", color: "#EC4899" },
              ].map((action) => {
                const Icon = action.icon;
                return (
                  <button
                    key={action.id}
                    onClick={() => setActiveView(action.id)}
                    className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-zinc-50 transition-all group border border-transparent hover:border-zinc-200"
                    data-testid={`action-${action.id}`}
                  >
                    <div className="p-2 border border-zinc-200 group-hover:border-black transition-colors" style={{ color: action.color }}>
                      <Icon size={14} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-bold uppercase tracking-wide">{action.label}</p>
                      <p className="text-[10px] text-zinc-400">{action.desc}</p>
                    </div>
                    <ArrowUpRight size={12} className="text-zinc-300 group-hover:text-black transition-colors" />
                  </button>
                );
              })}
            </div>
          </div>

          {/* Recent Programmes */}
          <div
            className={`border-2 border-zinc-200 bg-white transition-all duration-500 ${animReady ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}
            style={{ transitionDelay: "900ms" }}
            data-testid="recent-programmes"
          >
            <div className="px-5 py-4 border-b-2 border-zinc-200 flex items-center justify-between">
              <div>
                <h2 className="text-sm font-black font-['Chivo'] uppercase tracking-wide">Programmes</h2>
                <p className="text-[11px] text-zinc-400 mt-0.5">{schedules.length} imported baselines</p>
              </div>
              {schedules.length > 0 && (
                <Button
                  onClick={() => setActiveView("pm-integration")}
                  variant="outline"
                  className="text-[10px] px-3 py-1.5 h-auto uppercase tracking-wider border-2 border-zinc-200 hover:border-black"
                  data-testid="view-all-programmes"
                >
                  View All <ChevronRight size={10} className="ml-1" />
                </Button>
              )}
            </div>
            {schedules.length > 0 ? (
              <div className="divide-y divide-zinc-100">
                {schedules.slice(0, 6).map((s, i) => (
                  <div
                    key={s.id || i}
                    className="px-5 py-3 flex items-center gap-3 hover:bg-zinc-50 transition-colors group cursor-pointer"
                    onClick={() => setActiveView("pm-integration")}
                    data-testid={`programme-row-${i}`}
                  >
                    <div className="w-1 h-10 flex-shrink-0 bg-[#8B5CF6]" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-bold truncate group-hover:text-[#EAB308] transition-colors">
                        {s.project_name}
                      </p>
                      <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                        <span className="text-[9px] font-medium text-zinc-500 uppercase">
                          {s.source === "primavera_p6" ? "P6" : "MS Project"}
                        </span>
                        <span className="text-zinc-300">|</span>
                        <span className="text-[9px] text-zinc-400">{s.total_activities || 0} acts</span>
                        {(s.critical_activities || 0) > 0 && (
                          <>
                            <span className="text-zinc-300">|</span>
                            <span className="text-[9px] text-[#E11D48] font-bold">{s.critical_activities} critical</span>
                          </>
                        )}
                      </div>
                    </div>
                    {s.baseline_label && (
                      <Badge className="bg-[#8B5CF6]/10 text-[#8B5CF6] border border-[#8B5CF6]/30 text-[9px] uppercase tracking-wider flex-shrink-0">
                        {s.baseline_label}
                      </Badge>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-8 text-center">
                <Layers size={28} className="mx-auto mb-2 text-zinc-300" />
                <p className="text-[11px] font-bold text-zinc-500 mb-1">No Programmes Imported</p>
                <p className="text-[10px] text-zinc-400 mb-3">Import P6 / MS Project XML to start</p>
                <Button
                  onClick={() => setActiveView("pm-integration")}
                  className="text-[10px] px-3 py-1.5 h-auto bg-black text-white"
                  data-testid="empty-cta-import-programme"
                >
                  <Plus size={10} className="mr-1" /> Import Programme
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* ── COLLABORATION & CHANGE STRIP ── */}
        <div
          className={`border-2 border-zinc-200 bg-white p-5 transition-all duration-500 ${animReady ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}
          style={{ transitionDelay: "1000ms" }}
          data-testid="capabilities-bar"
        >
          <div className="flex flex-wrap items-center justify-between gap-4">
            <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">Platform Capabilities</span>
            {[
              { label: "Delay Forensic", icon: Clock, active: true },
              { label: "Programme Compare", icon: Layers, active: true },
              { label: "Claim Register", icon: Scale, active: true },
              { label: "Change & Variation", icon: GitBranch, active: true },
              { label: "Risk Heatmap", icon: Shield, active: true },
              { label: "Real-time Collab", icon: Radio, active: true },
            ].map((cap) => {
              const Icon = cap.icon;
              return (
                <div key={cap.label} className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 bg-[#10B981]" />
                  <Icon size={12} className="text-zinc-400" />
                  <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-zinc-500">{cap.label}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </TooltipProvider>
  );
};

export default DashboardView;
